import React, {useState, useRef} from 'react'
import styled from 'styled-components'
import MenuItem from '@mui/material/MenuItem'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import MenuList from '@mui/material/MenuList'
import Grow from '@mui/material/Grow'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Typography from '@mui/material/Typography'
import {getDefaultValue, getSimilarValues} from 'utils/utils'

const Input = styled.input`
    width: 100%;
    height: 100%;
    padding: 0 10px;
    box-sizing: border-box;
	opacity: ${props => props.lookup ? 0.5 : 1};

    + div {
        left: 0;
        right: 0;
        bottom: 0;
        content: "";
        position: absolute;
        transform: scaleX(0);
        transition: transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms;
        border-bottom: 2px solid #415dd4;
        pointer-events: none;
    }

    :focus {
        + div {
            transform: scaleX(1);
        }
    }
`

const Text = styled(Typography)`
	pointer-events: none;
`

const Menu = styled(Paper)`
	overflow: auto;
	max-height: 300px;
`

const TextField = ({defaultValue, environment, readonly, lookup, name, row, handleBlur}) => {
	const input = useRef(null)
	const [substitutions, onSearch] = useState([])

    const onBlur = (e) => {
		const el = e.relatedTarget

		if (el) {
			const value = el.getAttribute('data-autocomplete')

			if (value) {
				handleBlur(value, name)
				return
			}
		}
			
        if (e.target.value !== defaultValue) {
            handleBlur(e.target.value, name)
        }
    }

    const onFocus = (e) => {
        if (environment && !e.target.value) {
            const value = getDefaultValue(environment, name, row)
            if (value) {
                input.current.value = value
                input.current.select()
            }
        }
    }

    const onChange = (e) => {
        handleBlur(e.target.value, name)
    }

	const onInput = async (e) => {
		const string = e.target.value

		if (string.trim().length > 2) {
			const list = await getSimilarValues(name, row, string, environment)
			onSearch(list)
		} else {
			onClose()
		}
	}

	const onClose = () => {
		onSearch([])
	}

	const onSelect = (e) => {
		input.current.value = substitutions[e.target.value]
		onClose()
	}

	return (
		<React.Fragment>
				<Input
					ref={input}
					onBlur={environment ? onBlur : undefined}
					disabled={readonly}
					onFocus={onFocus}
					onChange={!environment ? onChange : undefined}
					defaultValue={defaultValue}
					lookup={lookup}
					onInput={environment ? onInput : undefined}
				/>
			<div />
			{!!substitutions.length &&
				<Popper
					popperOptions={{
						positionFixed: true
					}}
					sx={{
						zIndex: '1300'
					}}
					anchorEl={input.current}
					open={true}
					transition
				>
					{({TransitionProps}) => (
						<Grow {...TransitionProps}>
							<Menu elevation={8}>
								<ClickAwayListener onClickAway={onClose}>
									<MenuList
										sx={{
											width: input.current.clientWidth + 'px'
										}}
									>
										{substitutions.map((item, index) =>
											<MenuItem data-autocomplete={item} value={index} onClick={onSelect} key={index}>
												<Text variant='inherit' noWrap>
													{item}
												</Text>
											</MenuItem>
										)}
									</MenuList>
								</ClickAwayListener>
							</Menu>
						</Grow>
					)}
				</Popper>
			}
		</React.Fragment>
	)
}

export default React.memo(TextField)
